import { Card, CircularProgress, Grid, Pagination, Stack, Tab, Tabs } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { generateReportApiCall, fetchTemplateList, deleteTemplate } from "apis/coachup";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { enqueueSnackbar } from "notistack";
import DashboardLayout from "utils/LayoutContainers/DashboardLayout";
import DashboardNavbar from "utils/Navbars/DashboardNavbar";
import AssignTemplate from "./components/AssignTemplate";
import RenameTemplate from "./components/RenameTemplate";
import TemplateActions from "./components/TemplateActions";

const TEMPLATES_STATUS = {
  ACTIVE: "active",
  DRAFTED: "draft",
  ALYSIO_TEMPLATE: "alysio_template",
};

const STATIC_UPCOMING_TEMPLATES = ["QBR", "Coach Up"];

function Templates() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const [selectedTab, setSelectedTab] = useState(0);
  const { darkMode } = controller;
  const [templates, setTemplates] = useState({ data: [], count: 0 });
  const [isRenameTemplateModalOpen, setIsRenameTemplateModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [template, setTemplate] = useState();
  const [payload, setPayload] = useState({
    pageNumber: 0,
    pageSize: 9,
    searchBy: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (event, newValue) => {
    if (newValue === 0) {
      setPayload((prevState) => ({
        ...prevState,
        pageNumber: 0,
        status: TEMPLATES_STATUS.ACTIVE,
      }));
    } else if (newValue === 1) {
      setPayload((prevState) => ({
        ...prevState,
        pageNumber: 0,
        status: TEMPLATES_STATUS.DRAFTED,
      }));
    } else {
      setPayload((prevState) => ({
        ...prevState,
        pageNumber: 0,
        status: TEMPLATES_STATUS.ALYSIO_TEMPLATE,
      }));
    }
    setSelectedTab(newValue);
  };

  const getTemplates = async () => {
    try {
      setIsLoading(true);
      const response = await fetchTemplateList(payload);
      if (response?.data?.success) {
        setTemplates({ data: response?.data?.data?.rows, count: response?.data?.data?.count });
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
      setIsLoading(false);
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const generateReport = async (templateId) => {
    try {
      setIsLoading(true);
      const response = await generateReportApiCall({ templateId });
      if (response?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Report generated",
            text: response?.data?.message || "Report generated successfully.",
          }),
          {
            variant: "custom",
          }
        );
        setPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
        navigate(`/coach/reports`);
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
      setIsLoading(false);
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    getTemplates();
  }, [payload]);

  const handleTemplateDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to delete this template?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: theme.palette.primaryRed.main,
      cancelButtonColor: theme.palette.gray[80],
      confirmButtonText: "Yes, delete it!",
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          const response = await deleteTemplate(id);
          try {
            if (response?.data?.success) {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "success",
                  title: "Deleted",
                  text: response?.data?.message || "Team member successfully deleted.",
                }),
                {
                  variant: "custom",
                }
              );
              setPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
            } else {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "error",
                  title: "Error",
                  text: response?.response?.data?.message || "Something Went Wrong",
                }),
                {
                  variant: "custom",
                }
              );
              setPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
            }
          } catch (error) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: error.response.data.message || error.message || "Something went wrong",
              }),
              {
                variant: "custom",
              }
            );
          }
        }
      })
      .catch();
  };

  return (
    <DashboardLayout>
      {/* header */}
      <DashboardNavbar />
      <MDBox sx={{ position: "relative" }}>
        {isLoading ? (
          <Stack alignItems="center">
            <CircularProgress
              sx={{
                position: "fixed",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 2,
              }}
              color="info"
              size="10vh"
            />
          </Stack>
        ) : (
          ""
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={8} lg={8}>
            <MDBox minWidth={{ xs: "22rem", md: "25rem" }} sx={{ width: "25%" }}>
              <Tabs value={selectedTab} onChange={handleChange}>
                <Tab
                  id="monthly"
                  label={
                    <MDBox py={0.5} px={2} color="inherit">
                      Active
                    </MDBox>
                  }
                />
                <Tab
                  id="annual"
                  label={
                    <MDBox py={0.5} px={2} color="inherit">
                      Draft
                    </MDBox>
                  }
                />
                <Tab
                  id="annual"
                  label={
                    <MDBox py={0.5} px={2} color="inherit">
                      Alysio Templates
                    </MDBox>
                  }
                />
              </Tabs>
            </MDBox>
          </Grid>

          <Grid item xs={12} sm={12} md={4} lg={4} display="flex" justifyContent="flex-end">
            <MDBox>
              <MDButton
                type="submit"
                color="primaryRed"
                onClick={() => navigate("/coach/templates/add")}
              >
                + Add new template
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>

        <MDBox
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            "& .MuiInputLabel-formControl": { lineHeight: "15px" },
          }}
          mt={4}
          mb={4}
        >
          <MDBox pr={1}>
            <MDInput
              label="Search"
              value={payload.searchBy}
              onChange={(e) =>
                setPayload((prevState) => ({
                  ...prevState,
                  pageNumber: 0,
                  searchBy: e.target.value,
                }))
              }
            />
          </MDBox>
        </MDBox>
        <Grid
          item
          container
          xs={12}
          spacing={4}
          // direction="row"
          // justifyContent="center"
          // alignItems="center"
        >
          {templates.data.map((item) => (
            <Grid item xs={12} sm={12} md={6} lg={4} key={item.id}>
              <Card
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "20px",
                  cursor: item.disabled && "not-allowed",
                  background: item.disabled && "rgba(0, 0, 0, 0.05)",
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1.5} height="100%">
                  <MDBox sx={{ background: item.color, height: "inherit", width: "5px" }} />
                  <Stack p={1} direction="row" justifyContent="space-between" width="100%">
                    <Stack justifyContent="center">
                      <MDTypography variant="h5" component="h5">
                        {item.name}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        component="h6"
                        style={{ color: darkMode ? "white" : "rgba(0, 0, 0, 0.87)" }}
                      >
                        {item.description}
                      </MDTypography>
                    </Stack>
                    {!item.disabled && (
                      <Stack justifyContent="end">
                        <TemplateActions
                          setTemplate={setTemplate}
                          generateReport={generateReport}
                          displayGenerateReport={selectedTab !== 1}
                          template={item}
                          hideActions={selectedTab === 2}
                          deleteTemplate={handleTemplateDelete}
                          openRenameModal={() => setIsRenameTemplateModalOpen(true)}
                          openAssignModal={() => setIsAssignModalOpen(true)}
                        />
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Card>
            </Grid>
          ))}
          {selectedTab === 2 &&
            !isLoading &&
            STATIC_UPCOMING_TEMPLATES.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Grid item xs={12} sm={12} md={6} lg={4} key={index}>
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    padding: "10px",
                    cursor: "not-allowed",
                    background: "rgba(0, 0, 0, 0.05)",
                    height: "100%",
                  }}
                >
                  <Stack direction="row" spacing={1.5} height="100%">
                    <MDBox sx={{ background: "red", height: "inherit", width: "5px" }} />
                    <Stack p={1} direction="row" justifyContent="space-between" width="100%">
                      <Stack justifyContent="center">
                        <MDTypography variant="h5" component="h5">
                          {item}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          component="h6"
                          style={{ color: darkMode ? "white" : "rgba(0, 0, 0, 0.87)" }}
                        >
                          More pre populated templates coming soon!
                        </MDTypography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Card>
              </Grid>
            ))}
        </Grid>
        {templates.count > payload.pageSize && (
          <MDBox
            mt={2}
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Pagination
              count={Math.ceil(templates.count / payload.pageSize)}
              onChange={(_, page) =>
                setPayload((prevState) => ({ ...prevState, pageNumber: page - 1 }))
              }
              page={payload.pageNumber + 1}
            />
          </MDBox>
        )}
        <RenameTemplate
          open={isRenameTemplateModalOpen}
          setOpen={setIsRenameTemplateModalOpen}
          template={template}
          setTemplate={setTemplate}
          getTemplates={getTemplates}
        />
        <AssignTemplate
          open={isAssignModalOpen}
          setOpen={setIsAssignModalOpen}
          template={template}
          setTemplate={setTemplate}
          setPayload={setPayload}
          selectedTab={selectedTab}
        />
      </MDBox>
    </DashboardLayout>
  );
}

export default Templates;
